/**
 * Parkour-AI eTMF beside three trial master file products: Florence (eTMF and eBinders), Veeva Vault eTMF and Montrium
 * eTMF Connect. Ed chose to build eTMF on 2026-11-03, prompted by a device sponsor weighing Florence.
 *
 * ══ THE RULES THIS FILE IS WRITTEN UNDER ══ (Trace's, t1's and t4's)
 *
 * EVERY CELL IS SOURCED. Desk research on 2026-11-03 against public pages only, one URL per claim, listed in
 * SOURCES below. Nobody's product was signed into. Florence's own pages describe its eTMF in marketing terms; most of
 * what is known about its site product comes from training decks its customers published. Veeva's and Montrium's
 * columns rest on one product page each, so most of their cells are "Not established".
 *
 * "NOT ESTABLISHED" IS NOT "NO". A reading that did not settle a cell leaves it unknown, so the table is never wrong
 * about somebody else's product in our favour.
 *
 * OUR OWN COLUMN SAYS WHAT RUNS. eTMF was laid down on 2026-11-03 and nothing is built, so every cell of ours is a No
 * carrying the stance `planned`, naming the card that will fill it. capabilityExamples.test.ts holds this column to
 * the catalogue.
 */

export type Level =
    /** The product does this. */
    | "yes"
    /** It does part of it, or does it in a way that leaves the point of the row unmet. */
    | "partial"
    /** It does not, and that was established rather than assumed. */
    | "no"
    /** The reading did not establish it either way. NOT a polite "no". */
    | "unknown";

export const LEVEL_LABEL: Record<Level, string> = {
    yes: "Yes",
    partial: "Partly",
    no: "No",
    unknown: "Not established",
};

export type Cell = {
    level: Level;
    note?: string;
    /** On OUR cell only: a no we chose (`declined`), or a no that is a card on the roadmap (`planned`). */
    stance?: "declined" | "planned";
};

export type Contest = "contested" | "ceded" | "behind" | "ahead" | "neither" | "planned";

export const CONTEST_LABEL: Record<Contest, string> = {
    contested: "Head to head",
    ceded: "Ceded on purpose",
    behind: "Behind",
    ahead: "Ahead",
    neither: "Neither",
    planned: "On the roadmap",
};

export const PRODUCTS = ["Florence", "Veeva Vault eTMF", "Montrium"] as const;
export type Product = typeof PRODUCTS[number];

export type MatrixRow = {
    capability: string;
    note?: string;
    them: Record<Product, Cell>;
    ours: Cell;
};

const ANSWERS = (cell: Cell) => cell.level === "yes" || cell.level === "partial";

export function contestOf(row: MatrixRow): Contest {
    const them = PRODUCTS.some(product => ANSWERS(row.them[product]));
    const us = ANSWERS(row.ours);
    if (!us && row.ours.stance === "planned") return "planned";
    if (them && us) return "contested";
    if (them && !us) return row.ours.stance === "declined" ? "ceded" : "behind";
    if (!them && us) return "ahead";
    return "neither";
}

export type MatrixSection = {title: string; blurb: string; rows: MatrixRow[]};

export const SOURCES = [
    {product: "Florence", when: "3 November 2026",
     how: "florencehc.com: the eTMF, eBinders and SiteLink product pages, the compliance and security FAQ and the eBinders FAQ; Florence training material published by customers (Drexel's eBinders icon guide, October 2021; the Medical College of Wisconsin's eLogs deck; University Hospitals of Leicester's release FAQ, June 2026). G2 and Capterra refused automated reading, so review complaints are not used here."},
    {product: "Veeva Vault eTMF", when: "3 November 2026",
     how: "veeva.com/products/vault-etmf only."},
    {product: "Montrium", when: "3 November 2026",
     how: "montrium.com/etmf-connect only."},
    {product: "Parkour-AI · eTMF", when: "3 November 2026",
     how: "Laid down that day. Nothing is built: every cell of ours is a card on the roadmap, named in the cell."},
];

const yes = (note?: string): Cell => ({level: "yes", note});
const partial = (note?: string): Cell => ({level: "partial", note});
const unknown = (note?: string): Cell => ({level: "unknown", note});
const planned = (note: string): Cell => ({level: "no", note, stance: "planned"});

export const MATRIX: MatrixSection[] = [
    {
        title: "Structure and completeness",
        blurb: "How the file is organised, and how it knows what it is missing.",
        rows: [
            {capability: "Organised on the TMF Reference Model, with the version named",
             them: {Florence: partial("Templates and naming \"within the TMF structure\"; no model version named."), "Veeva Vault eTMF": unknown(), Montrium: yes("\"Fully aligned with version 3.3.1 of the TMF Reference Model\".")},
             ours: planned("Folders on the TMF Reference Model.")},
            {capability: "Expected documents (placeholders) generated per study and site",
             them: {Florence: partial("Placeholders and due dates in the site binders; missing documents shown in the eTMF."), "Veeva Vault eTMF": yes("Expected-document lists."), Montrium: unknown()},
             ours: planned("Expected documents, driven by milestones.")},
            {capability: "Completeness by study, country and site",
             them: {Florence: partial("Missing or expired documents and \"document health\"; levels not stated."), "Veeva Vault eTMF": unknown(), Montrium: yes("Completeness at study, country and site level.")},
             ours: planned("Completeness by study, country and site.")},
        ],
    },
    {
        title: "Filing and control",
        blurb: "Getting documents in, checking them, and signing them.",
        rows: [
            {capability: "Filing by email",
             them: {Florence: yes("\"Sync documents directly into the eTMF via email\"."), "Veeva Vault eTMF": unknown(), Montrium: yes("Site filing by email without an account.")},
             ours: planned("Filing by email, with unknown senders held.")},
            {capability: "Classification suggested from the document",
             them: {Florence: unknown("A third party says so; Florence's pages do not."), "Veeva Vault eTMF": yes("Auto-classification."), Montrium: unknown()},
             ours: planned("Classification, suggested and confirmed by a person.")},
            {capability: "A quality review step before a document counts",
             them: {Florence: yes("\"Built-in QC workflows\"; an automated page check (DocQC) beside it."), "Veeva Vault eTMF": yes("Risk-based QC sampling."), Montrium: unknown()},
             ours: planned("Draft, quality review, final.")},
            {capability: "Part 11 electronic signatures",
             them: {Florence: yes("\"Part 11 compliant eSignatures\"; a reason and a password or PIN on each."), "Veeva Vault eTMF": unknown(), Montrium: unknown()},
             ours: planned("Part 11 signatures.")},
            {capability: "Expiry dates tracked, with warnings",
             them: {Florence: yes("Expiration and due dates on documents; \"missing or expired\" shown."), "Veeva Vault eTMF": unknown(), Montrium: unknown()},
             ours: planned("Expiry and due dates.")},
            {capability: "Redaction of personal data",
             them: {Florence: yes("In-app redaction; documents marked as holding PHI."), "Veeva Vault eTMF": unknown(), Montrium: unknown()},
             ours: planned("Redaction and personal data.")},
        ],
    },
    {
        title: "Sites",
        blurb: "The site's own file and the logs it keeps.",
        rows: [
            {capability: "A site file kept apart from the sponsor's, with documents exchanged between them",
             them: {Florence: yes("eBinders at the site, synchronised with the sponsor's eTMF through SiteLink."), "Veeva Vault eTMF": unknown(), Montrium: unknown()},
             ours: planned("The site's own file, and documents exchanged with sites.")},
            {capability: "A delegation-of-authority log, signed and versioned per row",
             them: {Florence: yes("eLogs: DOA log templates, a new version per row edit, signature columns."), "Veeva Vault eTMF": unknown(), Montrium: unknown()},
             ours: planned("The delegation-of-authority log.")},
        ],
    },
    {
        title: "Access, inspection and exchange",
        blurb: "Who is let in, and how the file leaves.",
        rows: [
            {capability: "Permissions per folder",
             them: {Florence: yes("Binder- and folder-level permissions, with a permission overview."), "Veeva Vault eTMF": unknown(), Montrium: unknown()},
             ours: planned("Permissions on every folder.")},
            {capability: "Access for monitors, auditors and inspectors",
             them: {Florence: partial("Monitors review before visits and see redacted binders; an access-dates report."), "Veeva Vault eTMF": unknown(), Montrium: yes("Inspector access.")},
             ours: planned("Outside access with an end date, and a read-only view for an inspector.")},
            {capability: "Exchanging a whole file with another system",
             them: {Florence: unknown("Records and audit trails download; no exchange format named."), "Veeva Vault eTMF": partial("TMF Transfer between sponsors and CROs; the format is not named."), Montrium: partial("Excel exports.")},
             ours: planned("The whole file with its index, and the Reference Model's exchange format.")},
        ],
    },
];

/** Section titles to leave off the page; "*" hides the matrix entirely. A list rather than a
 *  deletion, so hiding is reversible and the data stays under test. */
export const HIDDEN_SECTIONS: string[] = [];

export const VISIBLE_MATRIX = () =>
    HIDDEN_SECTIONS.includes("*") ? [] : MATRIX.filter(section => !HIDDEN_SECTIONS.includes(section.title));
